import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faSlidersH, faThumbsUp, faUsers } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import AZ from '../../assets/logos/az.png';

// ---- Palette white / beige / glass ----
const colors = {
  bg: '#FDF8F0',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  accent: '#C69C6D',
  gold: '#C69C6D',
  cardBg: 'rgba(255, 255, 255, 0.65)',
  cardBorder: 'rgba(245, 235, 220, 0.6)',
  shadow: '0 8px 28px rgba(107, 90, 70, 0.08)',
  shadowHover: '0 12px 32px rgba(107, 90, 70, 0.12)',
  glassBlur: 'blur(14px)',
};

// ---- Variantes d'animation ----
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const FeaturesSection = () => {
  // Cartes des fonctionnalités
  const features = [
    { icon: faTrophy, title: "Réputation d'excellence" },
    { icon: faSlidersH, title: "Partenariats solides" },
    { icon: faThumbsUp, title: "Engagement absolu" },
    { icon: faUsers, title: "Équipe de professionnels" },
  ];

  return (
    <>
      <style>{`
        .ts-features {
          background: ${colors.bg};
          position: relative;
          overflow: hidden;
        }
        /* Fond décoratif très léger (optionnel) */
        .ts-features::after {
          content: '';
          position: absolute;
          top: -50px;
          left: -50px;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, ${colors.accent}15, transparent 70%);
          z-index: 0;
          border-radius: 50%;
        }
        .container {
          position: relative;
          z-index: 1;
        }
        h3 {
          font-size: clamp(1.5rem, 4vw, 2rem);
          font-weight: 400;
          color: ${colors.text};
          margin-bottom: 1rem;
          letter-spacing: -0.3px;
        }
        p {
          font-size: clamp(0.85rem, 2.5vw, 0.95rem);
          color: ${colors.textSoft};
          line-height: 1.65;
          margin-bottom: 1.5rem;
        }
        .logo-responsive {
          max-width: min(80%, 240px);
          filter: drop-shadow(0 4px 12px rgba(0,0,0,0.08));
          transition: transform 0.3s ease;
        }
        .logo-responsive:hover {
          transform: scale(1.03);
        }
        /* Cartes glass */
        .feature-card {
          background: ${colors.cardBg};
          backdrop-filter: ${colors.glassBlur};
          -webkit-backdrop-filter: ${colors.glassBlur};
          border: 1px solid ${colors.cardBorder};
          border-radius: 24px;
          padding: 1.2rem 1.5rem;
          height: 100%;
          box-shadow: ${colors.shadow};
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: default;
        }
        .feature-card:hover {
          transform: translateY(-3px);
          background: rgba(255, 255, 255, 0.75);
          border-color: rgba(198, 156, 109, 0.4);
          box-shadow: ${colors.shadowHover};
        }
        .feature-icon {
          font-size: 1.5rem;
          color: ${colors.gold};
          transition: transform 0.2s;
        }
        .feature-card:hover .feature-icon {
          transform: scale(1.08);
        }
        .feature-card h5 {
          font-size: 0.9rem;
          font-weight: 600;
          color: ${colors.text};
          margin: 0;
          letter-spacing: 0.01em;
        }
        @media (max-width: 768px) {
          .ts-features {
            padding: 3rem 0 !important;
          }
          .feature-card {
            padding: 1rem 1.2rem;
            border-radius: 20px;
          }
          .ts-features .col-lg-6:first-child {
            margin-bottom: 2rem;
          }
        }
      `}</style>

      <section className="ts-features py-5 py-md-6">
        <div className="container">
          <div className="row">
            {/* Logo centré */}
            <div className="col-12 text-center mb-5">
              <motion.img
                src={AZ}
                alt="Azyekos Logo"
                className="img-fluid logo-responsive"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                viewport={{ once: true }}
              />
            </div>

            {/* Colonne gauche – Qui sommes-nous + cartes */}
            <div className="col-lg-6">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <h3>Qui sommes-nous</h3>
                <p>
                  Azyekos Services est une Conciergerie de Luxe qui vous fournit des services et conseils sur mesure pour tous vos moments privés et professionnels. Dotée d'une grande capacité à cerner les moindres désirs et besoins de ses clients, Azyekos Services saura concrétiser vos souhaits et envies en vous offrant un service entièrement personnalisé. Elle vous libère des tracasseries du quotidien pour vous permettre de vivre des moments précieux en toute sérénité.
                </p>
              </motion.div>

              {/* Grille de cartes */}
              <motion.div
                className="row g-3 g-md-4"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {features.map((item, idx) => (
                  <motion.div className="col-md-6" key={idx} variants={itemVariants}>
                    <div className="feature-card d-flex align-items-center gap-3">
                      <span className="feature-icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </span>
                      <h5>{item.title}</h5>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Colonne droite – Valeurs & Mission */}
            <div className="col-lg-6 mt-5 mt-lg-0">
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <h3>Nos valeurs</h3>
                <p>
                  Chez Azyekos, nous valorisons l'excellence, la discrétion, l'élégance et la confiance. Nous offrons à nos clients un service personnalisé, raffiné et à la hauteur de leurs attentes les plus exigeantes.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
                viewport={{ once: true }}
              >
                <h3>Notre mission</h3>
                <p>
                  Notre mission est de vous offrir une expérience sur mesure alliant luxe, discrétion et excellence, pour sublimer chaque moment de vie personnel ou professionnel.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FeaturesSection;